import { z } from 'zod';

import {
  materialCaptureUnitValues,
  materialPresentationTypeValues,
} from '../materials-catalog/index.js';
import {
  materialCategoryRuleTypeValues,
  materialCategorySourceValues,
  type ListMaterialCategoriesRequest,
  type ListMaterialCategoriesResponse,
  type MaterialCategoryDto,
  type MaterialCategoryInput,
  type MaterialCategoryMember,
  type MaterialCategoryRule,
  type MaterialCategoryRuleType,
  type MaterialCategorySource,
  type MaterialCategoryWithMembersDto,
  type ReplaceMaterialCategoryMembersRequest,
  type ReplaceMaterialCategoryMembersResponse,
  type ResolveMaterialCategoriesRequest,
  type ResolveMaterialCategoriesResponse,
  type ResolvedMaterialCategoryDto,
  type UpsertMaterialCategoriesRequest,
  type UpsertMaterialCategoriesResponse,
} from './materials-categories.dto.js';

const timestampSchema = z.iso.datetime();
const dateSchema = z.iso.date();
const orderingSchema = z.number().int().min(0);
const MaterialPresentationTypeSchema = z.enum(materialPresentationTypeValues);
const MaterialCaptureUnitSchema = z.enum(materialCaptureUnitValues);

export const MaterialCategorySourceSchema: z.ZodType<MaterialCategorySource> = z.enum(
  materialCategorySourceValues,
);
export const MaterialCategoryRuleTypeSchema: z.ZodType<MaterialCategoryRuleType> = z.enum(
  materialCategoryRuleTypeValues,
);

const PresentationRuleSchema = z.object({
  type: z.literal('presentationTypes'),
  presentationTypes: z.array(MaterialPresentationTypeSchema).nonempty(),
});

const UnitRuleSchema = z.object({
  type: z.literal('unit'),
  unit: MaterialCaptureUnitSchema,
});

const CrvEligibleRuleSchema = z.object({
  type: z.literal('crvEligible'),
  isCrvEligible: z.boolean(),
});

export const MaterialCategoryRuleSchema: z.ZodType<MaterialCategoryRule> = z.union([
  PresentationRuleSchema,
  UnitRuleSchema,
  CrvEligibleRuleSchema,
]);

export const MaterialCategoryMemberSchema: z.ZodType<MaterialCategoryMember> = z.object({
  materialId: z.uuid(),
  presentationType: MaterialPresentationTypeSchema,
});

const membersSchema = z.array(MaterialCategoryMemberSchema).superRefine((items, ctx) => {
  const seen = new Set<string>();
  for (const item of items) {
    const key = `${item.materialId}:${item.presentationType}`;
    if (seen.has(key)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Duplicate materialId and presentationType entries are not allowed.',
      });
      return;
    }
    seen.add(key);
  }
});

const materialCategoryDtoSchema = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  stationId: z.uuid().nullable(),
  name: z.string().trim().min(1).max(200),
  sortOrder: orderingSchema,
  isActive: z.boolean(),
  rule: MaterialCategoryRuleSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const MaterialCategoryDtoSchema: z.ZodType<MaterialCategoryDto> = materialCategoryDtoSchema;

export const MaterialCategoryWithMembersDtoSchema: z.ZodType<MaterialCategoryWithMembersDto> =
  materialCategoryDtoSchema.extend({
    members: membersSchema,
  });

export const ResolvedMaterialCategoryDtoSchema: z.ZodType<ResolvedMaterialCategoryDto> = z.object({
  id: z.string().trim().min(1),
  name: z.string().trim().min(1).max(200),
  sortOrder: orderingSchema,
  isActive: z.boolean(),
  source: MaterialCategorySourceSchema,
  members: membersSchema,
});

export const ListMaterialCategoriesRequestSchema: z.ZodType<ListMaterialCategoriesRequest> =
  z.object({
    companyId: z.uuid(),
    stationId: z.uuid().nullable().optional(),
  });

export const ListMaterialCategoriesResponseSchema: z.ZodType<ListMaterialCategoriesResponse> =
  z.object({
    items: z.array(MaterialCategoryWithMembersDtoSchema),
  });

export const MaterialCategoryInputSchema: z.ZodType<MaterialCategoryInput> = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(200),
  sortOrder: orderingSchema,
  isActive: z.boolean(),
  rule: MaterialCategoryRuleSchema.nullable().optional(),
});

export const UpsertMaterialCategoriesRequestSchema: z.ZodType<UpsertMaterialCategoriesRequest> =
  z.object({
    companyId: z.uuid(),
    stationId: z.uuid().nullable().optional(),
    categories: z.array(MaterialCategoryInputSchema),
  });

export const UpsertMaterialCategoriesResponseSchema: z.ZodType<UpsertMaterialCategoriesResponse> =
  z.object({
    items: z.array(MaterialCategoryDtoSchema),
  });

export const ReplaceMaterialCategoryMembersRequestSchema: z.ZodType<ReplaceMaterialCategoryMembersRequest> =
  z.object({
    companyId: z.uuid(),
    categoryId: z.uuid(),
    stationId: z.uuid().nullable().optional(),
    members: membersSchema,
  });

export const ReplaceMaterialCategoryMembersResponseSchema: z.ZodType<ReplaceMaterialCategoryMembersResponse> =
  z.object({
    category: MaterialCategoryWithMembersDtoSchema,
  });

export const ResolveMaterialCategoriesRequestSchema: z.ZodType<ResolveMaterialCategoriesRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    effectiveOn: dateSchema.optional(),
    includeInactive: z.boolean().optional(),
  });

export const ResolveMaterialCategoriesResponseSchema: z.ZodType<ResolveMaterialCategoriesResponse> =
  z.object({
    items: z.array(ResolvedMaterialCategoryDtoSchema),
  });
